import React, { useEffect, useRef } from 'react';

import type { EChartsOption } from 'echarts';
import { BarChart, LineChart } from 'echarts/charts';
import { AriaComponent, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { init, use as registerECharts } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

registerECharts([
  BarChart,
  LineChart,
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type Props = {
  option: EChartsOption;
  title: string;
  source: string;
  range: string;
  onSelect?: (value: { product: string; date?: string }) => void;
  height?: number;
};

/** Native ECharts runtime; only the pure chart-option builder is shared with standalone. */
export default function ProductPerformanceTrend({ option, title, source, range, onSelect, height = 340 }: Props) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const element = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof init> | undefined>(undefined);
  const select = useRef(onSelect);
  useEffect(() => {
    select.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    if (!element.current) {
      return undefined;
    }
    const chart = init(element.current, undefined, { renderer: 'canvas' });
    instance.current = chart;
    chart.on('click', (event: { seriesName?: string; name?: string }) => {
      if (event.seriesName) {
        select.current?.({ product: event.seriesName, date: event.name });
      }
    });
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(element.current);
    return () => {
      observer.disconnect();
      chart.dispose();
      instance.current = undefined;
    };
  }, []);
  useEffect(() => {
    instance.current?.setOption(option, { notMerge: true });
  }, [option]);
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', minWidth: 0 }}>
      <Typography
        component="h2"
        variant="h6"
        sx={{
          px: 2,
          py: 1.5,
          fontSize: 17,
          bgcolor: tokens.plateHover,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {title}
      </Typography>
      <Box
        ref={element}
        role="img"
        aria-label={`${title}. ${range}. ${onSelect ? 'Select a series to filter.' : ''}`}
        sx={{ width: '100%', height, minWidth: 0 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', px: 2, pb: 1.5, overflowWrap: 'anywhere' }}
      >
        {source} · {range} · Sydney transaction date · Missing values remain gaps.
      </Typography>
    </Paper>
  );
}
